/**
 * Step4Technical Component
 *
 * Fourth step of assessment wizard - collects maintenance history and technical details
 */

import { useState } from 'react';
import { useRefrigerantInfo } from '../../hooks/useAssessment';

export default function Step4Technical({ state, setField, setEnvelopeField, errors }) {
  const [envelopeExpanded, setEnvelopeExpanded] = useState(false);
  const [lastRepairCostVisible, setLastRepairCostVisible] = useState(state.lastRepair !== 'never');

  const refrigerantInfo = useRefrigerantInfo(state.refrigerant);

  const handleLastRepairChange = (value) => {
    setField('lastRepair', value);
    setLastRepairCostVisible(value !== 'never');
    if (value === 'never') {
      setField('lastRepairCost', 0);
    }
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '30px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }}>
      <h2 style={{
        margin: '0 0 25px 0',
        color: '#2c3e50',
        fontSize: '24px'
      }}>
        Step 4: Maintenance & Technical Details
      </h2>

      {/* Last Repair */}
      <div style={{ marginBottom: '25px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontWeight: '600',
          color: '#2c3e50'
        }}>
          Previous Repairs (Before Today)
        </label>
        <select
          value={state.lastRepair}
          onChange={(e) => handleLastRepairChange(e.target.value)}
          style={{
            width: '100%',
            padding: '12px',
            fontSize: '16px',
            border: '2px solid #e0e6ed',
            borderRadius: '8px',
            outline: 'none',
            cursor: 'pointer',
            background: 'white'
          }}
          onFocus={(e) => e.target.style.borderColor = '#667eea'}
          onBlur={(e) => e.target.style.borderColor = '#e0e6ed'}
        >
          <option value="never">Never / No previous repairs</option>
          <option value="2+years">2+ years ago</option>
          <option value="1-2years">1–2 years ago</option>
          <option value="thisYear">This year (but not today)</option>
        </select>
      </div>

      {/* Last Repair Cost (conditional) */}
      {lastRepairCostVisible && (
        <div style={{ marginBottom: '25px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: '600',
            color: '#2c3e50'
          }}>
            Cost of Last Repair
          </label>
          <div style={{ position: 'relative' }}>
            <span style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#64748b',
              fontSize: '16px'
            }}>
              $
            </span>
            <input
              type="text"
              inputMode="decimal"
              min="0"
              step="50"
              value={state.lastRepairCost}
              onChange={(e) => {
                const value = e.target.value;
                // Allow empty string, numbers, and decimal point
                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                  setField('lastRepairCost', value === '' ? '' : value);
                }
              }}
              onBlur={(e) => {
                const value = e.target.value;
                const numValue = value === '' ? 0 : parseFloat(value);
                setField('lastRepairCost', isNaN(numValue) ? 0 : numValue);
              }}
              style={{
                width: '100%',
                padding: '12px 12px 12px 30px',
                fontSize: '16px',
                border: errors.lastRepairCost ? '2px solid #ef4444' : '2px solid #e0e6ed',
                borderRadius: '8px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
            />
          </div>
          {errors.lastRepairCost && (
            <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '5px' }}>
              {errors.lastRepairCost}
            </div>
          )}
        </div>
      )}

      {/* Refrigerant Type */}
      <div style={{ marginBottom: '25px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontWeight: '600',
          color: '#2c3e50'
        }}>
          Refrigerant Type
        </label>
        <select
          value={state.refrigerant}
          onChange={(e) => setField('refrigerant', e.target.value)}
          style={{
            width: '100%',
            padding: '12px',
            fontSize: '16px',
            border: errors.refrigerant ? '2px solid #ef4444' : '2px solid #e0e6ed',
            borderRadius: '8px',
            outline: 'none',
            cursor: 'pointer',
            background: 'white'
          }}
          onFocus={(e) => e.target.style.borderColor = '#667eea'}
          onBlur={(e) => e.target.style.borderColor = errors.refrigerant ? '#ef4444' : '#e0e6ed'}
        >
          <option value="">-- Select Refrigerant Type --</option>
          <option value="unsure">Not sure</option>
          <option value="r454b">R-454B (New Standard 2025+)</option>
          <option value="r410a">R-410A (Phasing Out)</option>
          <option value="r22">R-22 (Freon - Obsolete)</option>
        </select>
        {errors.refrigerant && (
          <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '5px' }}>
            {errors.refrigerant}
          </div>
        )}
        {refrigerantInfo && state.refrigerant !== 'unsure' && state.refrigerant !== '' && (
          <div style={{
            marginTop: '8px',
            padding: '12px',
            background: '#eff6ff',
            borderRadius: '6px',
            fontSize: '13px',
            color: '#1e40af'
          }}>
            ℹ️ {refrigerantInfo}
          </div>
        )}
      </div>

      {/* Original SEER */}
      <div style={{ marginBottom: '25px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontWeight: '600',
          color: '#2c3e50'
        }}>
          Original SEER Rating (Optional)
        </label>
        <input
          type="number"
          min="6"
          max="30"
          step="0.5"
          placeholder="Leave blank to estimate"
          value={state.originalSeer || ''}
          onChange={(e) => setField('originalSeer', e.target.value ? parseFloat(e.target.value) : null)}
          style={{
            width: '100%',
            padding: '12px',
            fontSize: '16px',
            border: errors.originalSeer ? '2px solid #ef4444' : '2px solid #e0e6ed',
            borderRadius: '8px',
            outline: 'none'
          }}
          onFocus={(e) => e.target.style.borderColor = '#667eea'}
          onBlur={(e) => e.target.style.borderColor = errors.originalSeer ? '#ef4444' : '#e0e6ed'}
        />
        <small style={{ color: '#64748b', fontSize: '13px', display: 'block', marginTop: '5px' }}>
          Check the data plate on your outdoor unit or leave blank for age-based estimate
        </small>
        {errors.originalSeer && (
          <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '5px' }}>
            {errors.originalSeer}
          </div>
        )}
      </div>

      {/* Duct Condition */}
      <div style={{ marginBottom: '25px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontWeight: '600',
          color: '#2c3e50'
        }}>
          Ductwork Condition
        </label>
        <select
          value={state.ductCondition}
          onChange={(e) => setField('ductCondition', e.target.value)}
          style={{
            width: '100%',
            padding: '12px',
            fontSize: '16px',
            border: '2px solid #e0e6ed',
            borderRadius: '8px',
            outline: 'none',
            cursor: 'pointer',
            background: 'white'
          }}
          onFocus={(e) => e.target.style.borderColor = '#667eea'}
          onBlur={(e) => e.target.style.borderColor = '#e0e6ed'}
        >
          <option value="excellent">Excellent (sealed, insulated)</option>
          <option value="good">Good (minor leaks)</option>
          <option value="fair">Fair (visible leaks)</option>
          <option value="poor">Poor (major issues)</option>
        </select>
      </div>

      {/* Home Envelope Section (Collapsible) */}
      <div style={{
        marginTop: '30px',
        padding: '20px',
        background: '#f8f9fa',
        borderRadius: '8px',
        border: '2px solid #e0e6ed'
      }}>
        <button
          type="button"
          onClick={() => setEnvelopeExpanded(!envelopeExpanded)}
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '0',
            fontWeight: '600',
            fontSize: '16px',
            color: '#2c3e50'
          }}
        >
          <span>🏠 Home Envelope Conditions (Optional - for better accuracy)</span>
          <span style={{
            transform: envelopeExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s',
            fontSize: '20px'
          }}>
            ▼
          </span>
        </button>

        {envelopeExpanded && (
          <div style={{ marginTop: '20px' }}>
            {/* Air Tightness */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#2c3e50'
              }}>
                Air Tightness
              </label>
              <select
                value={state.envelope.airTightness}
                onChange={(e) => setEnvelopeField('airTightness', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '2px solid #e0e6ed',
                  borderRadius: '8px',
                  outline: 'none',
                  cursor: 'pointer',
                  background: 'white'
                }}
              >
                <option value="well_sealed">Well Sealed (new construction, tested)</option>
                <option value="average">Average (typical home)</option>
                <option value="somewhat_drafty">Somewhat Drafty (older home)</option>
                <option value="very_drafty">Very Drafty (significant air leaks)</option>
              </select>
            </div>

            {/* Insulation Quality */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#2c3e50'
              }}>
                Insulation Quality
              </label>
              <select
                value={state.envelope.insulation}
                onChange={(e) => setEnvelopeField('insulation', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '2px solid #e0e6ed',
                  borderRadius: '8px',
                  outline: 'none',
                  cursor: 'pointer',
                  background: 'white'
                }}
              >
                <option value="good">Good (R-30+ attic, R-13+ walls)</option>
                <option value="average">Average (R-19 attic, R-11 walls)</option>
                <option value="fair">Fair (minimal insulation)</option>
                <option value="poor">Poor (little to no insulation)</option>
              </select>
            </div>

            {/* Window Type */}
            <div style={{ marginBottom: '0' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#2c3e50'
              }}>
                Window Type
              </label>
              <select
                value={state.envelope.windows}
                onChange={(e) => setEnvelopeField('windows', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '2px solid #e0e6ed',
                  borderRadius: '8px',
                  outline: 'none',
                  cursor: 'pointer',
                  background: 'white'
                }}
              >
                <option value="triple">Triple Pane (premium efficiency)</option>
                <option value="double_lowE">Double Pane Low-E (energy efficient)</option>
                <option value="double">Double Pane (standard)</option>
                <option value="single">Single Pane (older homes)</option>
              </select>
            </div>
          </div>
        )}

        {!envelopeExpanded && (
          <small style={{ color: '#64748b', fontSize: '13px', display: 'block', marginTop: '10px' }}>
            Using average home conditions for calculations. Click to customize for more accurate results.
          </small>
        )}
      </div>
    </div>
  );
}
